import { useState, useEffect } from 'react';
import { supabase } from '../lib/constants';
import { useAuth } from '../lib/auth';

export default function ComposeEmail({ onClose, isMobile }) {
  const { profile } = useAuth();
  const [fellows, setFellows]   = useState([]);
  const [faculty, setFaculty]   = useState([]);
  const [toType, setToType]     = useState('all_fellows');
  const [toIndividual, setToIndividual] = useState('');
  const [subject, setSubject]   = useState('');
  const [message, setMessage]   = useState('');
  const [sending, setSending]   = useState(false);
  const [sent, setSent]         = useState(false);
  const [error, setError]       = useState('');

  useEffect(() => {
    supabase.from('profiles').select('id, full_name, email, role')
      .in('role', ['fellow', 'faculty'])
      .then(({ data }) => {
        setFellows((data || []).filter(p => p.role === 'fellow'));
        setFaculty((data || []).filter(p => p.role === 'faculty'));
      });
  }, []);

  const getRecipients = () => {
    if (toType === 'all_fellows')        return fellows;
    if (toType === 'all_faculty')        return faculty;
    if (toType === 'all_fellows_faculty') return [...fellows, ...faculty];
    if (toType === 'individual') {
      const all = [...fellows, ...faculty];
      const person = all.find(p => p.id === toIndividual);
      return person ? [person] : [];
    }
    return [];
  };

  const send = async () => {
    if (!subject.trim()) { setError('Please enter a subject.'); return; }
    if (!message.trim()) { setError('Please enter a message.'); return; }
    const recipients = getRecipients();
    if (recipients.length === 0) { setError('No recipients selected.'); return; }
    setSending(true); setError('');

    const html = `
      <div style="font-family:Georgia,serif;max-width:600px;margin:0 auto;padding:32px;background:#F7F4F0;">
        <div style="background:#1A3A5C;padding:24px 28px;border-radius:8px;margin-bottom:24px;">
          <div style="color:#7EB8D4;font-size:20px;margin-bottom:4px;">✦ HPM Fellowship Program @ KHCC</div>
          <div style="color:#888;font-size:12px;text-transform:uppercase;letter-spacing:1px;">Message from the Program</div>
        </div>
        <div style="background:#fff;border-radius:8px;padding:22px 24px;margin-bottom:16px;">
          <div style="font-size:15px;color:#333;line-height:1.9;white-space:pre-wrap;">${message.replace(/\n/g, '<br/>')}</div>
        </div>
        <div style="text-align:center;font-size:11px;color:#aaa;margin-top:16px;">
          King Hussein Cancer Center · Hospice and Palliative Medicine Fellowship Program
        </div>
      </div>`;

    // Send one by one using the existing SQL function pattern
    let successCount = 0;
    for (const recipient of recipients) {
      const { error: e } = await supabase.rpc('send_custom_email', {
        recipient_email: recipient.email,
        recipient_name:  recipient.full_name,
        email_subject:   subject,
        email_html:      html,
      });
      if (!e) successCount++;
    }

    setSending(false);
    if (successCount === recipients.length) {
      setSent(true);
    } else {
      setError(`Sent to ${successCount} of ${recipients.length} recipients. Some may have failed.`);
    }
  };

  const recipientCount = getRecipients().length;
  const allPeople = [...fellows, ...faculty];

  if (sent) {
    return (
      <div style={s.overlay}>
        <div style={{ ...s.modal, textAlign: 'center', padding: '40px 32px' }}>
          <div style={{ fontSize: 48, marginBottom: 16 }}>✓</div>
          <div style={{ fontFamily: "'DM Serif Display'", fontSize: 22, color: '#1E5C40', marginBottom: 8 }}>Email Sent!</div>
          <div style={{ fontSize: 14, color: '#666', marginBottom: 28 }}>
            Your message was sent to {recipientCount} recipient{recipientCount !== 1 ? 's' : ''}.
          </div>
          <button onClick={onClose}
            style={{ padding: '12px 28px', background: '#1A3A5C', color: '#fff', border: 'none', borderRadius: 8, fontSize: 14, fontWeight: 600, cursor: 'pointer', fontFamily: "'DM Sans'" }}>
            Done
          </button>
        </div>
      </div>
    );
  }

  return (
    <div style={s.overlay}>
      <div style={s.modal}>
        {/* Header */}
        <div style={{ background: '#1A3A5C', padding: '16px 20px', borderRadius: '12px 12px 0 0', display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
          <span style={{ fontFamily: "'DM Serif Display'", fontSize: 18, color: '#F5F0E8' }}>✉ Compose Message</span>
          <button onClick={onClose} style={{ background: 'none', border: 'none', color: '#7EB8D4', fontSize: 20, cursor: 'pointer', lineHeight: 1 }}>✕</button>
        </div>

        <div style={{ padding: '20px 24px', overflowY: 'auto', maxHeight: isMobile ? '75vh' : '70vh' }}>
          {error && <div style={{ background: '#FFF0F0', border: '1px solid #F4A8A8', color: '#8A2D2D', padding: '10px 14px', borderRadius: 8, fontSize: 13, marginBottom: 16 }}>{error}</div>}

          {/* To */}
          <div style={s.field}>
            <label style={s.label}>To *</label>
            <select value={toType} onChange={e => { setToType(e.target.value); setToIndividual(''); }} style={s.input}>
              <option value='all_fellows'>All Fellows ({fellows.length})</option>
              <option value='all_faculty'>All Faculty ({faculty.length})</option>
              <option value='all_fellows_faculty'>All Fellows & Faculty ({fellows.length + faculty.length})</option>
              <option value='individual'>Individual person…</option>
            </select>
            {toType === 'individual' && (
              <select value={toIndividual} onChange={e => setToIndividual(e.target.value)} style={{ ...s.input, marginTop: 8 }}>
                <option value=''>— Select person —</option>
                <optgroup label="Fellows">
                  {fellows.map(f => <option key={f.id} value={f.id}>{f.full_name}</option>)}
                </optgroup>
                <optgroup label="Faculty">
                  {faculty.map(f => <option key={f.id} value={f.id}>{f.full_name}</option>)}
                </optgroup>
              </select>
            )}
            {recipientCount > 0 && (
              <div style={{ fontSize: 11, color: '#2D6A8A', marginTop: 6, fontWeight: 600 }}>
                📧 Will send to {recipientCount} recipient{recipientCount !== 1 ? 's' : ''}:
                {toType === 'individual' && toIndividual && (
                  <span style={{ marginLeft: 6 }}>{allPeople.find(p => p.id === toIndividual)?.full_name}</span>
                )}
              </div>
            )}
          </div>

          {/* Subject */}
          <div style={s.field}>
            <label style={s.label}>Subject *</label>
            <input value={subject} placeholder="e.g. Reminder: Please approve pending cases"
              onChange={e => setSubject(e.target.value)} style={s.input} />
          </div>

          {/* Quick templates */}
          <div style={s.field}>
            <label style={s.label}>Quick Templates</label>
            <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap' }}>
              {[
                { label: 'Approve entries', subject: 'Reminder: Pending cases awaiting your approval', msg: 'Dear Colleague,\n\nThis is a friendly reminder that there are pending cases in the logbook awaiting your review and approval.\n\nPlease log in at your earliest convenience to review and approve the submitted entries.\n\nThank you for your continued support of our fellows.\n\nBest regards,\nHPM Fellowship Program @ KHCC' },
                { label: 'Submit evaluations', subject: 'Reminder: Quarterly Evaluations Due', msg: 'Dear Faculty,\n\nThis is a reminder that quarterly evaluations are due. Please log in to the fellowship logbook and complete the evaluation for your assigned fellows.\n\nYour timely feedback is crucial for the fellows\' development and progress.\n\nThank you,\nHPM Fellowship Program @ KHCC' },
                { label: 'Log cases', subject: 'Reminder: Please Log Your Cases', msg: 'Dear Fellows,\n\nThis is a friendly reminder to keep your logbook up to date. Regular case logging is an essential part of your fellowship training and ensures accurate records of your clinical experience.\n\nPlease log in and record any recent cases at your earliest convenience.\n\nEvery case you log is a testament to your dedication and compassion.\n\nBest regards,\nHPM Fellowship Program @ KHCC' },
              ].map((t, i) => (
                <button key={i} onClick={() => { setSubject(t.subject); setMessage(t.msg); }}
                  style={{ padding: '6px 12px', background: '#EEF3FA', color: '#1A3A5C', border: '1px solid #A8C8DC', borderRadius: 20, fontSize: 12, fontWeight: 500, cursor: 'pointer', fontFamily: "'DM Sans'" }}>
                  {t.label}
                </button>
              ))}
            </div>
          </div>

          {/* Message */}
          <div style={s.field}>
            <label style={s.label}>Message *</label>
            <textarea value={message} rows={8} placeholder="Type your message here…"
              onChange={e => setMessage(e.target.value)}
              style={{ ...s.input, resize: 'vertical', lineHeight: 1.7 }} />
          </div>

          {/* Preview note */}
          <div style={{ background: '#F7F4F0', border: '1px solid #E0DDD8', borderRadius: 8, padding: '10px 14px', fontSize: 12, color: '#666', marginBottom: 8 }}>
            📧 Sent from: <strong>HPM Fellowship Program @ KHCC</strong>
          </div>
        </div>

        {/* Footer */}
        <div style={{ padding: '14px 20px', borderTop: '1px solid #F0EDE8', display: 'flex', gap: 10 }}>
          <button onClick={onClose}
            style={{ padding: '11px 18px', background: '#EEE', border: 'none', borderRadius: 8, fontSize: 13, cursor: 'pointer', fontFamily: "'DM Sans'" }}>
            Cancel
          </button>
          <button onClick={send} disabled={sending || recipientCount === 0}
            style={{ flex: 1, padding: '11px', background: recipientCount > 0 ? '#1A3A5C' : '#CCC', color: '#fff', border: 'none', borderRadius: 8, fontSize: 14, fontWeight: 600, cursor: recipientCount > 0 ? 'pointer' : 'default', opacity: sending ? 0.7 : 1, fontFamily: "'DM Sans'" }}>
            {sending ? `Sending to ${recipientCount}…` : `✉ Send to ${recipientCount} Recipient${recipientCount !== 1 ? 's' : ''}`}
          </button>
        </div>
      </div>
    </div>
  );
}

const s = {
  overlay: { position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.5)', zIndex: 1000, display: 'flex', alignItems: 'center', justifyContent: 'center', padding: 16 },
  modal: { background: '#fff', borderRadius: 12, width: '100%', maxWidth: 560, boxShadow: '0 20px 60px rgba(0,0,0,0.3)' },
  field: { marginBottom: 18 },
  label: { display: 'block', fontSize: 11, fontWeight: 600, color: '#555', marginBottom: 6, letterSpacing: 0.4, textTransform: 'uppercase' },
  input: { width: '100%', padding: '11px 13px', border: '1.5px solid #E0DDD8', borderRadius: 8, fontSize: 13, color: '#1A1A1F', background: '#fff', outline: 'none', fontFamily: "'DM Sans'", boxSizing: 'border-box' },
};
